import type { Icon, IconProps, IconWeight } from "@phosphor-icons/react";
import { forwardRef } from "react";

export interface AppIconProps extends Omit<IconProps, "alt" | "mirrored" | "ref" | "weight"> {
  decorative?: boolean;
}

export type AppIcon = ReturnType<typeof createAppIcon>;

export function createAppIcon(SourceIcon: Icon, weight: IconWeight = "regular") {
  const AppIconComponent = forwardRef<SVGSVGElement, AppIconProps>(
    (
      {
        decorative,
        size = "1em",
        focusable,
        role,
        "aria-hidden": ariaHidden,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        ...props
      },
      ref,
    ) => {
      const hasAccessibleName = Boolean(ariaLabel || ariaLabelledBy);
      const isDecorative = decorative ?? !hasAccessibleName;

      return (
        <SourceIcon
          {...props}
          ref={ref}
          size={size}
          weight={weight}
          focusable={focusable ?? false}
          role={role ?? (isDecorative ? undefined : "img")}
          aria-hidden={ariaHidden ?? isDecorative}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        />
      );
    },
  );

  AppIconComponent.displayName = `App${SourceIcon.displayName ?? "Icon"}`;

  return AppIconComponent;
}
